import { isSupportedCurrency, currencyInfo } from '~/utils/currency'
import { useAccountStore } from '~/store/account'

/**
 * Adopt the currency a visitor arrived with as the session currency.
 *
 * Google Things to Do resolves the `{currency}` macro in our feed's landing_page URL to the
 * currency it showed the user, exactly as Google Hotel Ads does with `?currency=`. The hotel
 * property page has honoured this since the GHA price-accuracy work; the experiences pages
 * never did, so every Things to Do click landed in USD no matter what price Google had just
 * displayed — the same mismatch class that suppressed hotel listings.
 *
 * MUST be called before the page's useAsyncData, so the X-TRIPENING-CURRENCY header on the
 * product request already carries it and the SSR HTML (which is what Google's checker reads)
 * is priced correctly on the first paint.
 *
 * Unresolved macros arrive literally — `?currency={currency}` or percent-encoded
 * `%7Bcurrency%7D` — whenever Google does not substitute. isSupportedCurrency rejects those,
 * along with any currency the API currency middleware would 400 on.
 */
export function useLandingCurrency() {
  const route = useRoute()
  const accountStore = useAccountStore()
  const currencyCookie = useCookie('currency')

  const raw = route.query?.['currency'] ?? route.query?.['google-displayed-currency']
  const code = String(Array.isArray(raw) ? raw[0] ?? '' : raw ?? '').toUpperCase()

  if (!isSupportedCurrency(code) || currencyCookie.value === code) return

  currencyCookie.value = code
  accountStore.setCurrency(currencyInfo(code))

  // Setting the cookie ref is NOT enough during SSR. Every useCookie('currency') call builds a
  // fresh ref by parsing the INCOMING request's cookie header, so useApi — which reads its own
  // ref to set X-TRIPENING-CURRENCY — still saw the old value and fetched the product in USD.
  // The response carried Set-Cookie: currency=EUR while the HTML it accompanied was priced in
  // dollars. Rewrite the request header so reads later in this same render see the new value.
  if (import.meta.server) {
    const event = useRequestEvent()
    if (event?.node?.req?.headers) {
      const kept = String(event.node.req.headers.cookie || '')
        .split(';')
        .map((part) => part.trim())
        .filter((part) => part && !part.startsWith('currency='))
      kept.push(`currency=${code}`)
      event.node.req.headers.cookie = kept.join('; ')
    }
  }
}
